<script setup lang="ts">
import type { Step } from "#bb/types/blocks/content/StepperOption";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, addItem, removeItem, updateItem } = useInlineBlockEditor(props.id);

const steps = computed<Step[]>(() => block.value?.steps ?? []);
const statuses: Step["status"][] = ["upcoming", "completed", "active"];

const stepPath = (step: Partial<Step>) => step.path ?? step.resourcePath ?? step.resourceId ?? "";

const addStep = () =>
  addItem("steps", {
    id: crypto.randomUUID(),
    title: "",
    description: "",
    path: "",
    status: "upcoming",
  });
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:circle-arrow-right" accent="navigation">
    <p class="text-[11.5px] text-muted-foreground">{{ t('description') }}</p>

    <div class="space-y-2">
      <div
        v-for="(step, index) in steps"
        :key="step.id ?? index"
        class="space-y-2 rounded-sm border bg-card p-2"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="truncate text-[12.5px] font-medium">
            {{ step.title || t('stepTitle', { number: index + 1 }) }}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            class="size-7 shrink-0 text-destructive"
            @click="removeItem('steps', index)"
          >
            <Icon mode="svg" size="15" name="lucide:trash-2" />
          </Button>
        </div>

        <Input
          :model-value="step.title"
          class="h-8 rounded-sm bg-muted text-[13px]"
          :placeholder="t('fields.title.placeholder')"
          @update:model-value="(v) => updateItem('steps', index, { title: v })"
        />
        <Input
          :model-value="step.description"
          class="h-8 rounded-sm bg-muted text-[13px]"
          :placeholder="t('fields.description.placeholder')"
          @update:model-value="(v) => updateItem('steps', index, { description: v })"
        />
        <EditorPathInput
          :model-value="stepPath(step)"
          mode="existing-file"
          :scopes="['site']"
          :placeholder="t('fields.path.placeholder')"
          aria-label="Step resource path"
          @update:model-value="(v) => updateItem('steps', index, { path: v })"
        />
        <Select
          :model-value="step.status ?? 'upcoming'"
          @update:model-value="(v) => updateItem('steps', index, { status: v })"
        >
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
            <SelectValue :placeholder="t('fields.status.label')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="status in statuses" :key="status" :value="status">
              {{ t(`fields.statuses.${status}`) }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button type="button" variant="outline" size="sm" class="w-full rounded-sm text-[13px]" @click="addStep">
        <Icon mode="svg" size="16" name="lucide:plus" class="mr-1.5" />
        {{ t('fields.addStep.label') }}
      </Button>
    </div>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Stepper"
  description: "Add and order the steps shown in this navigation progress component."
  stepTitle: "Step {number}"
  fields:
    title:
      placeholder: "Enter a step title"
    description:
      placeholder: "Enter a description"
    path:
      placeholder: "Enter a resource file path (for example /reports/summary)"
    status:
      label: "Status"
    statuses:
      upcoming: "Upcoming"
      completed: "Completed"
      active: "Active"
    addStep:
      label: "Add step"
</i18n>
